import { useEffect, useMemo } from 'react';
import {
  AdditionalDocument,
  DeliveryMethod,
} from 'src/components/tso-document-list';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { Tso } from 'src/types/monolith';
import { Forbidden, NotFound } from 'src/utils/errors';
import urlTemplate from 'url-template';

export type CommunicationInfo = {
  phone?: string;
  phoneBackup?: string;
  phoneOnCall?: string;
  mobile?: string;
  fax?: string;
  email?: string;
};

export type ContactType =
  | 'DISPATCHING'
  | 'BILLING'
  | 'COMMUNICATION'
  | 'OPERATION';

export type ContactPerson = {
  id: number;
  type?: ContactType;
  salutation?: 'MR' | 'MRS';
  title?: string;
  section?: string;
  firstName?: string;
  lastName?: string;
  communication?: CommunicationInfo;
};

export function useTso(id: string | number) {
  const tso = useAxios(
    (axios, signal, id: string | number) =>
      axios.request<Tso>({
        signal,
        url: `${PRISMA_CONFIG.oldBackendUrl}/tso/${id}`,
        method: 'GET',
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // sadly we get a 500 in case of an unknown id
        if (isServerError(error) && error.response.status === 500) {
          throw new NotFound();
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    tso.execute(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return tso;
}

export type TsoRequest = ReturnType<typeof useTso>;

export type AssignmentDocumentsResponse = {
  creditRatingCertification?: DeliveryMethod;
  creditRatingCertificationAgency?: DeliveryMethod;
  creditSolvencyCheckDocument?: DeliveryMethod;
  mostRecentInterimFinancialReport?: DeliveryMethod;
  mostRecentFullYearFinancialReport?: DeliveryMethod;
  currentCompanyReport?: DeliveryMethod;
  companyRegisterExtract?: DeliveryMethod;
  prismaShipperAdminPowerOfAttorney?: DeliveryMethod;
  additionalDocuments?: AdditionalDocument[];
  additionalUserDocuments?: AdditionalDocument[];
};

export function useAssignmentDocuments(id: string, url?: string | null) {
  const documents = useAxios(
    (axios, signal, id: string) => {
      if (!url) throw new Forbidden();

      return axios.request<AssignmentDocumentsResponse>({
        signal,
        url: urlTemplate.parse(url).expand({ tsoId: id }),
        method: 'GET',
        validateStatus: (status) =>
          // status 404 means there is no documents found
          status === 200 || status === 404,
      });
    },
    {
      neededOnPageLoad: true,
      onError(error) {
        if (error instanceof Forbidden) {
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    documents.execute(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return documents;
}

export type AssignmentDocumentsRequest = ReturnType<
  typeof useAssignmentDocuments
>;

export function useTsoDocuments(tso: TsoRequest) {
  const documents = useMemo(() => {
    if (!tso.response) {
      return [];
    }

    const mandateCheckDocuments =
      tso.response.data.mandateCheckDocumentList.map<AdditionalDocument>(
        (document) => ({
          additionalDocumentName: document.name,
          url: PRISMA_CONFIG.monolithUrl + document.url,
          registrationType: DeliveryMethod.None,
          customConfiguration: null,
          template: undefined,
        })
      );

    const solvencyCheckDocuments =
      tso.response.data.solvencyCheckDocumentList.map<AdditionalDocument>(
        (document) => ({
          additionalDocumentName: document.name,
          url: PRISMA_CONFIG.monolithUrl + document.url,
          registrationType: DeliveryMethod.None,
          customConfiguration: null,
          template: undefined,
        })
      );

    const miscDocuments =
      tso.response.data.miscDocumentList.map<AdditionalDocument>(
        (document) => ({
          additionalDocumentName: document.name,
          url: PRISMA_CONFIG.monolithUrl + document.url,
          registrationType: DeliveryMethod.None,
          customConfiguration: null,
          template: undefined,
        })
      );

    return [
      ...mandateCheckDocuments,
      ...solvencyCheckDocuments,
      ...miscDocuments,
    ];
  }, [tso]);

  return documents;
}
